import { defineStore } from 'pinia';
import {
    resolveTheme,
    systemPrefersDark,
    applyTheme,
    rememberLastApplied,
    readCachedPreference,
    writeCachedPreference,
    fetchPreference,
    savePreference,
    watchSystemTheme,
    isAllowedPreference
} from '@/services/themeService';

// Detach handle for the OS listener. Module-scoped rather than store state because it is a
// function, not data -- putting it in state would make Pinia try to make a listener reactive.
let unwatchSystem = null;

// Counts EXPLICIT user choices (the ones that persist). hydrate() samples it before its
// network read and re-checks afterwards, so a preference the user picked while that read
// was in flight is never overwritten by the older stored value coming back.
//
// Module-scoped and deliberately not reactive: nothing renders from it, and it must survive
// independently of store state so the comparison is against "did a human choose", not
// "did the value change" -- re-choosing the SAME preference still counts.
let explicitChoices = 0;

// Pinia store for the user's appearance preference. Follows the same shape as the other
// stores here (featureFlags hydrates from cache first, then the network) so the menu does
// not flash while the API call resolves.
//
// Components read THIS. They never touch localStorage or axios directly -- all of that is
// owned by services/themeService.js, which is deliberately the single swappable seam.
export const useThemeStore = defineStore('theme', {
    state: () => ({
        // 'light' | 'dark' | 'system' -- what the user chose.
        preference: 'light',
        // 'light' | 'dark' -- what is actually on screen right now.
        resolved: 'light',
        // Used to key the per-user cache. Null until the user resolves.
        userId: null
    }),
    getters: {
        isDark: state => state.resolved === 'dark'
    },
    actions: {
        /**
         * Apply `preference` immediately, cache it, and persist it.
         *
         * `persist: false` is for values that CAME from storage (cache or server) -- writing
         * them straight back would be a pointless round-trip, and on the server path would
         * turn every page load into a POST.
         */
        async setPreference(preference, { persist = true } = {}) {
            if (!isAllowedPreference(preference)) return;

            // `persist` is what distinguishes a human choosing from a stored value being
            // replayed, so it doubles as the signal for the hydrate race guard.
            if (persist) explicitChoices++;

            this.preference = preference;
            this.applyCurrent();

            // Only 'system' should track the OS. Detach first so an explicit choice never
            // leaves a stale listener that would later override the user.
            if (unwatchSystem) {
                unwatchSystem();
                unwatchSystem = null;
            }
            // NOTE the attach happens after the save, further down -- only the DETACH is here.
            // Detaching immediately is required (a stale listener would later override an
            // explicit choice); attaching immediately was a hole: the listener writes durable
            // state, so hanging it before the save meant an OS flip could record 'system' as
            // the remembered theme even though the POST that chose 'system' had failed.

            // Persist BEFORE remembering, so nothing durable can hold a value the server
            // rejected. On a failed POST the cache used to keep the new preference while the
            // server kept the old one, so the next load painted the cached value and the
            // hydrate reconcile then reverted it -- a dark-to-light flip seconds into the
            // session with nothing on screen to explain it.
            //
            // BOTH durable writes have to be on this side of the await. Moving only the
            // per-user cache was not enough: applyTheme used to write the pre-paint key too,
            // which is why that write now lives in rememberLastApplied instead.
            //
            // A throw here propagates to ThemeMenu.choose(), which swallows it deliberately:
            // the theme is applied for this session, it just will not follow the user to
            // another machine or survive a reload.
            if (persist) {
                const choicesAtSave = explicitChoices;
                await savePreference(preference);
                // A newer choice was made while this POST was in flight. Its own save will
                // record the durable state; writing ours now would persist the older value
                // if the responses came back out of order. Introduced by moving the writes
                // after the await -- the previous order could not produce it.
                if (explicitChoices !== choicesAtSave) return;
            }

            // Attach the OS listener only once the preference is durable, because the
            // listener itself writes durable state on every OS flip. Attaching it before the
            // await meant a failed save could still leave 'system' remembered. The cost when
            // a save fails is that 'system' stops tracking the OS for the rest of the
            // session -- acceptable, since nothing was persisted either way.
            if (preference === 'system') {
                unwatchSystem = watchSystemTheme(() => {
                    this.applyCurrent();
                    // An OS flip under a SAVED 'system' preference is authoritative: the
                    // preference did not change and needs no POST, but the next pre-paint
                    // should match what the OS shows now or every load flashes the old scheme.
                    rememberLastApplied(this.resolved);
                });
            }

            if (this.userId) writeCachedPreference(this.userId, preference);
            rememberLastApplied(this.resolved);
        },

        applyCurrent() {
            this.resolved = resolveTheme(this.preference, systemPrefersDark());
            applyTheme(this.resolved);
        },

        /**
         * Called once the signed-in user is known. Applies the cached preference immediately,
         * then reconciles against the server, which is the source of truth -- that is what
         * makes the choice follow the user to a new browser or machine.
         */
        async hydrate(userId) {
            this.userId = userId;

            const cached = readCachedPreference(userId);
            if (isAllowedPreference(cached)) {
                await this.setPreference(cached, { persist: false });
            }

            // Sampled BEFORE the network read. hydrate() is called un-awaited from the
            // router guard, so the app is interactive while this request is in flight --
            // long enough for someone to open the menu and pick a theme. Without this
            // guard the older server value came back and reverted their choice, and
            // overwrote the per-user cache with it, leaving the UI and the server
            // disagreeing until the next reload.
            const choicesBeforeFetch = explicitChoices;

            try {
                const serverPreference = await fetchPreference();
                // No `!== this.preference` guard here, deliberately. The pre-paint script in
                // index.html stamps data-theme from `mosaic.theme.lastApplied`, which is NOT
                // per-user -- it cannot be, because it runs before we know who is signing in.
                // So on a shared workstation the DOM can already say "dark" from the PREVIOUS
                // user while this user's stored preference is "light", which also equals the
                // store default. Skipping the apply because "it already matches" compared the
                // store to the server and ignored the DOM, leaving the new user in dark
                // permanently rather than for one frame.
                if (explicitChoices !== choicesBeforeFetch) {
                    // The user chose while we were reading, so their choice is newer than
                    // anything this response can tell us. Leave it alone.
                    //
                    // Note the save may still be in flight, or may have FAILED -- an earlier
                    // version of this comment asserted it had "already been POSTed", which is
                    // not true in either case. It does not need to be: a human decision made
                    // 200ms ago beats a stored value read before they made it, whether or not
                    // the write succeeded. If it failed, nothing durable recorded it and the
                    // next load falls back to the server's value, which is the honest outcome.
                } else if (isAllowedPreference(serverPreference)) {
                    await this.setPreference(serverPreference, { persist: false });
                }
            } catch {
                // An API without the endpoint yet, or an offline blip. The cached preference
                // stands. Never block app boot on a preference.
            } finally {
                // Whatever happened above -- server, cache, or neither -- the DOM must end up
                // agreeing with store state. This is the only line that guarantees a stale
                // pre-paint attribute is cleared.
                this.applyCurrent();

                // The pre-paint key gets corrected too, but ONLY if no explicit choice
                // happened while we were reading.
                //
                // Unconditionally was wrong, and not theoretically: if the user picks a theme
                // during hydrate and that POST then fails, setPreference correctly declines to
                // record anything -- and this line went and recorded it anyway, from a value
                // that reached store state optimistically. That is the same next-load flip the
                // save-ordering work exists to prevent, arriving by a different door.
                //
                // When a choice did happen, its own setPreference owns the durable state: it
                // writes on success and deliberately writes nothing on failure.
                if (explicitChoices === choicesBeforeFetch) {
                    rememberLastApplied(this.resolved);
                }
            }
        }
    }
});
